/**
 * Section: Cart drawer
 * -----------------------------------------------------------------------------
 * Contains highly coupled scripts to render cart items onto a drawer.
 *
 * @namespace cartDrawer
 */
import * as cart from '@shopify/theme-cart';
import {
  formatMoney
} from '@shopify/theme-currency';
import {
  getSizedImageUrl
} from '@shopify/theme-images';
import {
  register
} from '@shopify/theme-sections';
import {
  disableBodyScroll,
  enableBodyScroll
} from 'body-scroll-lock';

import cssClasses from '~/helpers/cssClasses';
import {
  on,
  decodeHTMLEntities,
  handleize
} from '~/helpers/utils';
import Toggle from '~/components/toggle';
import QuantitySelector from '~/components/quantity-selector';
import engravingLineItems from '~/components/engraving-line-items';


const fixEngravingsCountInCart = async function () {
  await cart.getState().then((cartData) => {
    let engravingProductCount = 0;
    let engravingServicesCount = 0;
    let engravingServices = [];
    const engravingServiceId = theme && theme.engraving && theme.engraving.product ? theme.engraving.product : false;
    const lineItemsToUpdate = [];

    // Se NON è attivo IL SERVIZIO di engraving rimuovi tutti i prodotti e i servizi engraving
    if (!engravingServiceId) {
      cartData.items.map((item) => {
        if (item.properties && item.properties['ENGRAVING']) {
          lineItemsToUpdate.push({
            key: item.key,
            quantity: 0
          });
        } else if (item.properties && item.properties['_engraving-service']) {
          lineItemsToUpdate.push({
            key: item.key,
            quantity: 0
          });
        };
      });
    };

    // Conta i PRODOTTI engraving
    cartData.items.map((item) => {
      if (item.properties && item.properties['ENGRAVING']) {
        engravingProductCount++;
      };
    });

    engravingServices = cartData.items.filter(item => item.properties && item.properties['_engraving-service']); // Estrai i SERVIZI engraving
    engravingServicesCount = engravingServices.length ? engravingServices.reduce((count, engravingService) => (count + engravingService.quantity), 0) : 0;

    // Se ci sono PRODOTTI engraving
    if (engravingProductCount) {
      engravingServices
        .map((item, i) => {
          lineItemsToUpdate.push({
            key: item.key,
            quantity: i > 0 ? 0 : engravingProductCount,
          });
        });

      // Se NON ci sono SERVIZI di engraving ma ci sono PRODOTTI engraving
      if (!engravingServicesCount && engravingProductCount) {

        // Aggiungi UN SOLO SERVIZIO di engraving per la quantità di PRODOTTI con engraving
        cart.addItem(engravingServiceId, {
          quantity: engravingProductCount,
          properties: {
            '_engraving-service': 'Yes',
          },
        });
      };
    } else { // Se non c'è nessun PRODOTTO engraving rimuovi i SERVIZI engraving
      engravingServices
        .map(item => { // Rimuovi dal carrello i SERVIZI extra
          lineItemsToUpdate.push({
            key: item.key,
            quantity: 0
          });
        });
    }

    // Esci se non c'è nulla da aggiornare
    if (!lineItemsToUpdate) {
      return;
    }

    if (lineItemsToUpdate.length) {
      engravingLineItems().updateEngravingLineItems(lineItemsToUpdate);
    }
  })
    .catch((error) => {
      throw new Error('Could not get cart data', error);
    });
}

/**
 * DOM selectors.
 */
const selectors = {
  body: '[js-cart-drawer="body"]',
  cartAddOn: '[js-cart-drawer="cartAddOn"]',
  container: '[js-cart-drawer="container"]',
  footer: '[js-cart-drawer="footer"]',
  lineItem: '[js-ajax-cart="lineItem"]',
  productJson: '[js-product-json]',
  subtotal: '[js-ajax-cart="subtotal"]',
  quantitySelector: '[js-ajax-cart="quantitySelector"]',
  productsLinks: '[js-ajax-cart="productLink"]',
};

/**
 * State selectors.
 */
const stateSelectors = {
  cartAddOn: 'has-cart-add-on',
};

/**
 * Attribute and data selectors.
 */
const dataSelectors = {
  ajaxCart: 'js-ajax-cart',
};

/**
 * Create a new drawer instance.
 */
const Drawer = Toggle({
  toggleSelector: 'cartDrawer',
});

/**
 * Instance globals.
 */
let quantitySelectorTimer = {};

/**
 * Register the `cart-drawer` section type.
 */
register('cart-drawer', {

  /**
   * Initialise section.
   */
  init() {
    fixEngravingsCountInCart();
    Drawer.init();
    this.setNodeSelectors();
    this.setRemoveEvents(this.container);
    this.constructQuantitySelectors();
    this.dataLayerLinks();
  },

  /**
   * Set cached node selectors.
   */
  setNodeSelectors() {
    this.body = document.querySelector(selectors.body);
    this.container = document.querySelector(selectors.container);
    this.footer = document.querySelector(selectors.footer);
    this.subtotal = document.querySelector(selectors.subtotal);

    if (theme.features.cartAddOn) {
      this.cartAddOn = document.querySelector(selectors.cartAddOn);
    }
  },

  /**
   * Set EventBus listeners for each instance.
   */
  setEventBusListeners() {
    Frame.EventBus.listen('Toggle:cartDrawer:open', () => {
      document.body.classList.add('cart-open');
      disableBodyScroll(this.body);
    });
    Frame.EventBus.listen('Toggle:cartDrawer:close', () => {
      this.handleDrawerClose();

      window.dataLayer.push({
        event: 'GAevent',
        eventID: '18',
        eventCategory: 'cart',
        eventAction: 'minicart_interaction',
        eventLabel: 'close_minicart',
      });
    });
    Frame.EventBus.listen('AjaxCart:updated', (response) => this.handleDrawerOpen(response));
    Frame.EventBus.listen('AjaxCart:removingItem', (item) => this.setLoadingState(item));
    Frame.EventBus.listen('AjaxCart:cartEmpty', () => this.setEmptyState());
  },

  /**
   * Set listeners for datalayer events.
   */
  dataLayerLinks() {
    on('click', this.body, (event) => {
      if (!event.target.closest(selectors.productsLinks)) {
        return;
      }

      window.dataLayer.push({
        event: 'GAevent',
        eventID: '18',
        eventCategory: 'cart',
        eventAction: 'minicart_interaction',
        eventLabel: 'go_to_product',
      });
    });
  },


  /**
   * Instantiate quantity selectors using id.
   * - Callback has a timer to reduce number of calls.
   */
  constructQuantitySelectors() {
    const quantitySelectors = this.container.querySelectorAll(selectors.quantitySelector);

    [...quantitySelectors].forEach((item) => {
      QuantitySelector(`${item.id}`, () => {
        window.clearTimeout(quantitySelectorTimer);
        quantitySelectorTimer = window.setTimeout(() => {
          this.handleQuantityUpdate(item);
        }, 250);
      }).setInitState();
    });
  },

  /**
   * Handle quantity updates.
   * @param {HTMLElement} element - Quantity selector node element.
   */
  handleQuantityUpdate(element) {
    this.setRenderingState();

    const data = {
      index: element.closest(selectors.lineItem).dataset.index,
      key: element.closest(selectors.lineItem).dataset.key,
      quantity: parseInt(element.querySelector('input').value, 10),
    };

    cart.updateItem(data.key, {
      quantity: data.quantity
    })
      .then(() => {
        Frame.EventBus.emit('AjaxCart:itemUpdated');
      })
      .catch((error) => {
        if (typeof response === 'undefined') {
          return;
        }

        throw new Error('Could not update item data', error);
      });
  },

  /**
   * Conditionally handle drawer open.
   * @param {Object} data - Cart.js data.
   */
  handleDrawerOpen(data) {
    if (theme.pageType === 'cart') {
      return;
    }

    if (data.item_count === 0) {
      this.render(data);
      return;
    }

    Drawer.open(this.container);
    document.body.classList.add('cart-open');
    disableBodyScroll(this.body);
    this.setRenderingState();
    this.render(data);
  },

  /**
   * Conditionally handle drawer close.
   */
  handleDrawerClose() {
    enableBodyScroll(this.body);
    document.body.classList.remove('cart-open');
  },

  updateCompareAtPrices({
    lineItem,
    compareAtPrice
  }) {
    const compareAtPlaceholder = lineItem.querySelector('[data-compare-at-price]');
    if (compareAtPlaceholder) {
      compareAtPlaceholder.setAttribute('data-compare-at-price', compareAtPrice);
      compareAtPlaceholder.style = 'display: block;';
      compareAtPlaceholder.innerHTML = compareAtPrice;
    }
  },

  updateCompareAtPricesCache({
    key,
    compareAtPrice
  }) {
    const updatableCacheEntry = window.AlessiCartPrices.find(priceObj => priceObj.key == key);

    if (!updatableCacheEntry) {
      window.AlessiCartPrices.push({
        "key": key,
        "compare_at_price": compareAtPrice,
      })
    }
  },

  /**
   * Render line items compare at prices.
   * @param {Object} data - Cart.js data.
   */
  async loadCompareAtPrices(data) {
    if (window.AlessiCartPrices) {
      for (let i = 0; i < data.items.length; ++i) {
        const currentItem = data.items[i];

        // Using this to update cache at the end
        let currentItemComparePrice = '';

        const currentItemPrices = window.AlessiCartPrices.find(priceObj => priceObj.key == currentItem.key);

        if (currentItemPrices) {
          const lineItem = document.querySelector(`.ajax-cart__line-item[data-key="${currentItemPrices.key}"]`);
          currentItemComparePrice = currentItemPrices.compare_at_price;

          // Skip when compare at price is not set
          if (currentItemComparePrice == 0) {
            this.updateCompareAtPricesCache({
              key: currentItem.key,
              compareAtPrice: currentItemComparePrice
            });

            continue;
          }

          if (lineItem) {
            this.updateCompareAtPrices({
              lineItem,
              compareAtPrice: currentItemComparePrice
            });
          }
        } else {
          const productData = await fetch(`/products/${currentItem.handle}.js`).then(res => res.json());

          if (productData && productData.variants.length) {
            const currentVariant = productData.variants.find(variant => variant.id == currentItem.id);

            // Skip when compare at price is not set
            if (!currentVariant.compare_at_price || currentVariant.compare_at_price == 0) {
              this.updateCompareAtPricesCache({
                key: currentItem.key,
                compareAtPrice: currentVariant.compare_at_price
              });

              continue;
            }

            const lineItem = document.querySelector(`.ajax-cart__line-item[data-id="${currentVariant.id}"]`);
            currentItemComparePrice = window.theme && window.theme.moneyFormat ? formatMoney(currentVariant.compare_at_price, window.theme.moneyFormat) : currentVariant.compare_at_price;

            if (lineItem) {
              this.updateCompareAtPrices({
                lineItem,
                compareAtPrice: currentItemComparePrice
              });
            }
          }
        }

        this.updateCompareAtPricesCache({
          key: currentItem.key,
          compareAtPrice: currentItemComparePrice
        });
      }
    }
  },

  /**
   * Render line items template.
   * @param {Object} data - Cart.js data.
   */
  async render(data) {
    this.setActiveState(data);
    this.constructQuantitySelectors();

    if (theme.features.cartAddOn) {
      this.handleCartAddOnState(data);
    }

    await this.loadCompareAtPrices(data);

    if (theme.features.currencyConverter) {
      Frame.EventBus.emit('Currency:convertAll');
    }
  },

  /**
   * Set active state.
   * @param {Object} data - Cart.js data.
   */
  setActiveState(data) {
    this.body.innerHTML = this.getActiveTemplate(data.items);
    this.subtotal.setAttribute('data-price', data.total_price);
    this.subtotal.innerHTML = formatMoney(data.total_price, theme.moneyFormat);
    this.footer.classList.remove(cssClasses.hidden);

    window.setTimeout(() => {
      this.container.classList.remove(cssClasses.loading);
      this.dataLayerLinks();
    }, 300);
  },

  /**
   * Set event delegation to remove items on container.
   * @param {HTMLElement} container - The container to apply the remove events on.
   */
  setRemoveEvents(container) {
    on('click', container, (event) => this.handleRemoveEvent(event));
  },

  /**
   * Handle remove events.
   * @param {Event} event - Click event.
   */
  handleRemoveEvent(event) {
    if (!this.isTargetRemove(event.target)) {
      return;
    }

    event.preventDefault();
    Frame.EventBus.emit('AjaxCart:removingItem', event.target.closest(selectors.lineItem));

    const data = {
      dataLayer: event.target.closest(selectors.lineItem).querySelector(selectors.productJson).innerHTML,
    };

    cart.removeItem(event.target.dataset.key)
      .then((response) => {
        Frame.EventBus.emit('AjaxCart:itemRemoved', response);
        return response;
      })
      .catch((error) => {
        window.console.error(error);
        return error;
      });

    window.dataLayer.push({
      event: 'removeFromCart',
      ecommerce: {
        currencyCode: window.theme.currency,
        eventPageLocator: 'minicart',
        remove: {
          actionField: {
            list: '',
          },
          products: [JSON.parse(data.dataLayer)],
        },
      },
    });
  },

  /**
   * Detect remove target click.
   * @param {HTMLElement} target - Clicked target to check remove attributes against.
   * @returns {Boolean}
   */
  isTargetRemove(target) {
    return (
      typeof target.attributes[dataSelectors.ajaxCart] !== 'undefined' &&
      target.attributes[dataSelectors.ajaxCart].nodeValue === 'remove'
    );
  },

  /**
   * Set loading state.
   * @param {Object} element - DOM line item.
   */
  setLoadingState(element) {
    element.classList.add(cssClasses.removing);
  },

  /**
   * Set empty state.
   */
  setEmptyState() {
    this.body.innerHTML = this.getEmptyTemplate();
    this.footer.classList.add(cssClasses.hidden);

    if (theme.features.cartAddOn) {
      this.hideCartAddOn();
    }
  },

  /**
   * Get cart drawer body template.
   * @param {Object} items - Cart line item object data.
   * @returns {String}
   */
  getActiveTemplate(items) {
    return items.map((item, index) => {
      if (this.isAddOnItem(item) || this.isServices(item)) {
        return '';
      }

      return this.getLineItemTemplate(item, index);
    }).join('');
  },

  /**
   * Get empty cart template.
   * @returns {String}
   */
  getEmptyTemplate() {
    return `<p>${decodeHTMLEntities(theme.strings.cart.general.empty)}</p>`;
  },

  /**
   * Get line item template.
   * @param {Object} item - Cart line item object data.
   * @param {Integer} index - Line item index.
   * @returns {string}
   */
  getLineItemTemplate(item, index) {
    return `
      <div
        class="ajax-cart__line-item"
        data-index="${index}"
        data-key="${item.key}"
        data-id="${item.id}"
        js-ajax-cart="lineItem"
      >
        ${this.getLineItemImage(item)}

        <div class="ajax-cart__description">
          <div class="row no-margin no-gutter">
            <div class="col xs9 m9">
              <div class="ajax-cart__inner">
                ${this.isVendorEnabled() ? this.getVendorTitle(item) : ''}

                <a class="ajax-cart__title" href="${item.url}" js-ajax-cart="productLink">${item.product_title}</a>

                ${this.getVariantTitle(item)}
                ${this.getEngravingMessage(item)}

                <div class="ajax-cart__amount">
                  <div class="ajax-cart__quantity">
                    ${item.quantity} x&nbsp;
                  </div>

                  <div class="ajax-cart__price">
                    ${this.getLineItemPrice(item)}
                    <s data-compare-at-price="" style="display:none;"></s>
                  </div>
                </div>
              </div>
            </div>

            <div class="col xs3 m3">
              ${this.isGiftProduct(item) ? '' : this.getRemoveButton(item)}
            </div>
          </div>
        </div>

        <script js-product-json>
          {
            "name": "${handleize(item.product_title).replace(/-/g, '_')}",
            "id": "${item.variant_id}",
            "price": ${(item.price / 100)},
            "brand": "alessi",
            "variant": "${(item.variant_options[0] && !item.product_has_only_default_variant) ? item.variant_options[0] : ``}",
            "quantity": ${item.quantity},
            "dimension16": "${(item.variant_options[1]) ? item.variant_options[1] : ``}"
          }
        </script>
      </div>
    `;
  },

  /**
   * Get line item image template.
   * @param {Object} item - Variant line item object from cart.js.
   * @returns {String}
   */
  getLineItemImage(item) {
    if (!item.image) {
      return '';
    }

    return `
    <div class="ajax-cart__image responsive-image__wrapper">
      <div class="ajax-cart__image-container">
        <img
          alt="${item.title}"
          sizes="auto"
          src="${getSizedImageUrl(item.image, '50x')}"
          srcset="
            ${getSizedImageUrl(item.image, '120x')} 120w,
            ${getSizedImageUrl(item.image, '240x')} 240w,
            ${getSizedImageUrl(item.image, '320x')} 320w
          "
        >
      </div>
    </div>
    `;
  },


  /**
   * Get variant price template.
   * - Checks if it's discounted or not.
   * @param {Object} item - Cart line item to get price from.
   * @returns {String}
   */
  getLineItemPrice(item) {
    if (item.properties && item.properties.ENGRAVING) {
      var item_discounted_price = item.discounted_price + theme.engraving.price;
      var item_price = item.price + theme.engraving.price;
      var item_original_price = item.original_price + theme.engraving.price;
    } else {
      var item_discounted_price = item.discounted_price;
      var item_price = item.price;
      var item_original_price = item.original_price;
    }
    if (item.original_line_price !== item.line_price) {
      if (item.discounted_price !== item.price) {
        return `
          <span class="visually-hidden">${item_discounted_price}</span>

          <span data-price="${item_discounted_price}" js-currency="price">
            ${formatMoney(item_discounted_price, theme.moneyFormat)}
          </span>

          <span class="visually-hidden">${item_price}</span>

          <s data-price="${item_original_price}" js-currency="compareAtPrice">
            ${formatMoney(item_original_price, theme.moneyFormat)}
          </s>

        `;
      }

      return `
        <span class="visually-hidden">${item_price}</span>

        <span data-price="${item_price}" js-currency="price">
          ${formatMoney(item_price, theme.moneyFormat)}
        </span>

        <span class="visually-hidden">${item_original_price}</span>
        <s>${formatMoney(item_original_price, theme.moneyFormat)}</s>
      `;
    }

    return `
      <span data-price="${item_price}" js-currency="price">
        ${formatMoney(item_price, theme.moneyFormat)}
      </span>
    `;
  },

  /**
   * Get variant titles.
   * @param {Object} items - Cart line item object data.
   * @returns {String}
   */
  getVariantTitle(item) {
    return this.isVariantTitle(item) ?
      `<p class="ajax-cart__variant-title caption">${item.variant_title}</p>` :
      '';
  },

  /**
   * Get engraving message.
   * @param {Object} item - Line item data object.
   */

  getEngravingMessage(item) {
    let engravingProperty = '';

    if (item.properties !== null) {
      if (item.properties.ENGRAVING !== undefined) {
        engravingProperty = `
            <p class="cart-table__large-message caption">
              <span>${theme.engraving.label} ${item.properties.ENGRAVING}</span>
            </p>
          `;
      }
    }

    return engravingProperty;
  },

  /**
   * Get the template for the line item remove button.
   * @param {Object} item - Cart line item object data.
   * @returns {String}
   */
  getRemoveButton(item) {
    return `
      <button
        class="ajax-cart__remove"
        tabindex="0"
        type="button"
        data-key="${item.key}"
        js-ajax-cart="remove"
      >
        ${theme.icons.close}

        <span class="visually-hidden">
          ${theme.strings.remove}
        </span>
      </button>
    `;
  },

  /**
   * Check if the line item is a gift product.
   * @param {Object} item - Cart line item object data.
   * @returns {Boolean}
   */
  isGiftProduct(item) {
    return Boolean(item.properties && item.properties['_gift-product']);
  },

  /**
   * Check if current item is an add-on.
   * @param {Object} item - Cart variant item.
   * @returns {Boolean}
   */
  isAddOnItem(item) {
    if (!item) {
      throw Error('Could not find item');
    }

    return (item.product_type.toLowerCase() === 'add-on');
  },
  /**
   * Check if current item is a services.
   * @param {Object} item - Cart variant item.
   * @returns {Boolean}
   */
  isServices(item) {
    if (!item) {
      throw Error('Could not find item');
    }

    return (item.product_type.toLowerCase() === 'services');
  },

  /**
   * Check if vendor title setting is enabled.
   * @returns {Boolean}
   */
  isVendorEnabled() {
    return JSON.parse(this.container.dataset.enableVendorTitle);
  },

  /**
   * Get vendor title template.
   * @param {Object} item - Cart item to get vendor title from.
   * @returns {String}
   */
  getVendorTitle(item) {
    return `<p class="ajax-cart__vendor caption">${item.vendor}</p>`;
  },

  /**
   * Check if variant title exists.
   * @param {Object} item - Cart line item object data.
   * @returns {Boolean}
   */
  isVariantTitle(item) {
    return item.variant_options.length && !item.variant_options[0].includes('Default Title');
  },

  /**
   * Check if variant title exists.
   * @param {Object} item - Cart line item object data.
   * @returns {Boolean}
   */
  isLineItems(item) {
    return item.properties.length;
  },

  /**
   * Toggle on/off visibility of cart add-on note.
   * @param {Object} data - Cart data.
   */
  handleCartAddOnState(data) {
    if (!this.isAddOnInCart(data)) {
      this.hideCartAddOn();
      return;
    }

    this.showCartAddOn();
  },

  /**
   * Check if cart has an add-on product.
   * @param {Object} data - Cart data.
   * @returns {Array|Boolean}
   */
  isAddOnInCart(data) {
    return data.items.some((item) => this.isAddOnItem(item));
  },

  /**
   * Show cart add-on note and re-adjust footer resize.
   */
  showCartAddOn() {
    this.cartAddOn.classList.remove(cssClasses.hidden);
    this.container.classList.add(stateSelectors.cartAddOn);
  },

  /**
   * Hide cart add-on note.
   */
  hideCartAddOn() {
    this.cartAddOn.classList.add(cssClasses.hidden);
    this.container.classList.remove(stateSelectors.cartAddOn);
  },

  /**
   * Check if cart has an engraving product.
   * @param {Object} data - Cart data.
   * @returns {Array|Boolean}
   */
  isEngravingInCart(data) {
    return data.items.some((item) => this.isServices(item));
  },

  /**
   * Set the rendering state of the drawer.
   */
  setRenderingState() {
    this.container.classList.add(cssClasses.loading);
  },

  /**
   * Close drawer.
   */
  close() {
    Drawer.close(this.container);
  },

  /**
   * Callback function when section is selected by
   * the Theme Editor `shopify:section:select` event.
   */
  onSelect() {
    Frame.EventBus.emit('AjaxCart:requestUpdate');
  },

  /**
   * Callback function when section is deselected by
   * the Theme Editor `shopify:section:deselect` event.
   */
  onDeselect() {
    this.close();
  },

  /**
   * Callback function when section is loaded via `sections.load()`
   * or by the Theme Editor `shopify:section:load` event.
   */
  onLoad() {
    this.init();
    this.setEventBusListeners();
  },
});
